<template>
    <div class="comments-section">
        <h3 class="fs-16 fw-bold clr-title mb-4">Comments</h3>
        <!-- Loading state -->
        <div v-if="loading" class="text-center p-4">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading comments...</span>
            </div>
        </div>

        <!-- Comments List -->
        <div v-else class="comment_listwrapper">
            <div :class="['comment_list', { scrollable: comments.length > 4 }]">
                <div v-for="(comment) in comments" :key="comment.id">
                    <div class="comment_item d-flex align-items-start gap-3">
                        <div class="avatar_user flex-shrink-0">
                            <img src="../../../assets/images/user.svg" width="46" height="46" class="rounded-circle" />
                        </div>
                        <div class="comment_content d-flex flex-column gap-1" style="width: 100%; max-width: 100%;">
                            <div class="comment_header d-flex align-items-center gap-3 flex-wrap">
                                <span class="comment_author fs-16 clr-black fw-semibold">{{ comment.commented_by?.name
                                    || 'Unknown User' }}</span>
                                <span class="comment_date clr-bold-grey font-13">{{ formatDate(comment.created_at) ||
                                    'Unknown date' }}</span>

                            </div>
                            <div class="comment_text fs-16 mb-0" v-html="comment.comment || 'No content'"
                                style="width: 100%;"></div>
                            <template v-if="comment?.document && comment.document.length > 0">
                                <div class="comment_document mt-3">
                                    <p class="fs-16 clr-bold-grey mb-3">Documents:</p>
                                    <div class="row">
                                        <div v-for="document in comment.document" :key="document.id" class="col-md-6 col-lg-4 mb-4">
                                            <div class="ticket_documentInfo d-flex align-items-center gap-3 h-100 p-3 border rounded">
                                                <div class="icon-wrapper flex-shrink-0">
                                                    <img src="./../../../assets/images/quotes.svg" alt="document icon" class="w-100 h-100" />
                                                </div>
                                                <div class="document_content d-flex flex-column gap-1 flex-grow-1">
                                                    <a :href="document.full_file_url" target="_blank"
                                                        class="document_link d-flex align-items-center gap-1 justify-content-between text-decoration-none">
                                                        <span class="clr-primary fw-semibold text-truncate">
                                                            {{ document.original_file_name }}
                                                        </span>
                                                        <span class="link_icon d-flex align-items-center justify-content-center clr-primary font-14">
                                                            <i class="fa-solid fa-arrow-up-right-from-square"></i>
                                                        </span>
                                                    </a>
                                                    <div class="document_info d-flex align-items-center gap-2 flex-wrap font-14">
                                                        <span class="dots"></span>
                                                        <span class="clr-title fw-medium">
                                                            Uploaded:
                                                            <span class="clr-bold-grey fw-normal">{{ formatDate(document.updated_at || document.created_at) }}</span>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                    <hr>
                </div>

                <!-- No comments message -->
                <div v-if="!comments || comments.length === 0" class="text-center p-4 text-muted">
                    No comments available
                </div>
            </div>
        </div>

        <!-- Comment Input -->
        <div class="mt-4 mb-4">
            <input-quill-editor :vmodel="newComment" @update:vmodel="newComment = $event" ref="commentEditor"
                placeholder="Add your message here ..." :error="commentError" formGroupClass="editorInputWrapper"
                @ready="onEditorReady" />
        </div>
        <div class="button-wrapper d-flex justify-content-end align-items-center gap-3 flex-wrap">
            <label for="fileInput" class="btn attach-btn d-flex align-items-center">
                <i class="fas fa-paperclip mr-2"></i> Attach File
            </label>
            <span class="file-name">
                {{form.comment_document.length ? form.comment_document.map(f => f.name).join(', ') : 'No file chosen'
                }}
            </span>
            <input type="file" id="fileInput" @change="handleFileChange" hidden multiple>
            <b-button class="univ-btn" type="button" @click="submitComment" :disabled="submitting || !addComment">
                <span v-if="submitting">
                    <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                    Submitting...
                </span>
                <span v-else>Comment</span>
            </b-button>
        </div>

        <!-- Error display -->
        <div v-if="commentError" class="text-danger small mt-2">
            {{ commentError }}
        </div>
        <div v-if="error" class="alert alert-danger mt-3" role="alert">
            {{ error }}
        </div>
    </div>
</template>

<script>
import InputQuillEditor from "@/components/form/InputQuillEditor";
import Helper from '@/resource/Helper';
import axios from 'axios';

export default {
    name: "TicketIssueComment",
    components: {
        InputQuillEditor
    },
    props: {
        ticketData: {
            type: Object,
            required: true
        },
        addComment: {
            type: Boolean,
            required: true
        },
        formatDate: {
            type: Function,
            default: null
        }
    },
    data() {
        return {
            comments: [],
            newComment: "",
            loading: false,
            submitting: false,
            error: null,
            commentError: '',
            quillInstance: null,
            form: {
                comment_document: []
            },
            fileError: ''
        };
    },
    computed: {
        issueId() {
            return this.$route.params.ticketId;
        },
        agentId() {
            return this.$store.getters.repId;
        }
    },
    mounted() {
        this.fetchComments();
    },
    methods: {
        async fetchComments() {
            this.loading = true;
            this.error = null;

            try {
                const response = await axios.get(
                    `${process.env.VUE_APP_TICKETING_URL}v2/agent/issues/${this.issueId}/comments`,
                    {
                        headers: {
                            'X-Authorization': Helper.messageCenterToken(),
                            'X-Ticketing-Token': Helper.ticketingToken()
                        }
                    }
                );

                if (response.data && response.data.data) {
                    const responseData = response.data.data;
                    if (Array.isArray(responseData)) {
                        this.comments = responseData;
                    } else if (responseData.data && Array.isArray(responseData.data)) {
                        this.comments = responseData.data;
                    } else if (responseData.comments) {
                        this.comments = responseData.comments;
                    } else {
                        this.comments = [];
                        console.warn('Unexpected data structure in response:', responseData);
                    }
                } else {
                    this.comments = [];
                    console.warn('No data found in response:', response.data);
                }

            } catch (error) {
                console.error('Error fetching comments:', error);
                this.error = 'Failed to load comments. Please try again.';
                this.comments = [];
            } finally {
                this.loading = false;
            }
        },
        handleInput(value) {
            this.newComment = value || '';
            this.clearError();
        },
        clearError() {
            this.commentError = '';
            this.fileError = '';
            this.error = '';
        },
        handleFileChange(event) {
            this.clearError();
            const files = event.target.files;
            if (!files || files.length === 0) return;

            this.form.comment_document = Array.from(files);
        },
        getCommentContent() {
            let content = '';
            if (this.quillInstance) {
                content = this.quillInstance.root.innerHTML;
            } else {
                content = this.newComment;
            }
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content;
            const hasContent = content &&
                content.trim() !== '' &&
                tempDiv.textContent.trim() !== '';

            return hasContent ? content : '';
        },

        onEditorReady(quill) {
            this.quillInstance = quill;
            if (this.newComment) {
                quill.clipboard.dangerouslyPasteHTML(this.newComment);
            }
        },

        isCommentEmpty() {
            const content = this.getCommentContent();

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = content || '';
            const hasTextContent = tempDiv.textContent.trim() !== '';

            return !hasTextContent;
        },

        validateComment() {
            const isEmpty = this.isCommentEmpty();
            if (isEmpty) {
                this.commentError = 'Please enter a comment';
                return false;
            }


            return true;
        },

        clearEditor() {
            this.newComment = '';
            this.$nextTick(() => {
                if (this.quillInstance) {
                    this.quillInstance.setContents([]);
                    this.quillInstance.focus();
                }
            });
            this.clearError();

            const fileInput = document.getElementById('fileInput');
            if (fileInput) {
                fileInput.value = '';
            }
        },

        async submitComment() {
            if (!this.validateComment()) {
                return;
            }
            if (!this.addComment) {
                return;
            }

            this.submitting = true;
            this.error = null;
            const loader = this.$loading.show();

            try {
                const commentContent = this.getCommentContent();
                const strippedContent = commentContent.replace(/<[^>]*>/g, '');

                // Prepare form data for file upload
                const formData = new FormData();
                formData.append('comment', strippedContent);
                formData.append('agent_id', this.agentId);
                formData.append('issue_id', this.issueId);

                // Add files if any
                if (this.form.comment_document && this.form.comment_document.length > 0) {
                    this.form.comment_document.forEach((file, index) => {
                        formData.append(`comment_document[${index}]`, file);
                    });
                }

                const response = await axios.post(
                    `${process.env.VUE_APP_TICKETING_URL}v2/agent/submit-comment`,
                    formData,
                    {
                        headers: {
                            'X-Authorization': Helper.messageCenterToken(),
                            'X-Ticketing-Token': Helper.ticketingToken(),
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                if (response.data.success) {
                    // Clear the editor and form
                    this.clearEditor();
                    this.form.comment_document = [];
                    document.getElementById('fileInput').value = '';

                    // Show success message
                    this.$notify('Comment added successfully', "success");


                    // Fetch updated comments
                    await this.fetchComments();
                } else {
                    throw new Error(response.data.message || 'Failed to submit comment');
                }
            } catch (error) {
                console.error('Error submitting comment:', error);
                const errorMessage = error.response?.data?.message || 'An error occurred while submitting your comment';
                this.error = errorMessage;
                this.$notify(errorMessage, "error");
                console.error('Error submitting comment:', error);
            } finally {
                this.submitting = false;
                if (loader && loader.hide) {
                    loader.hide();
                }
            }
        },
    }
};
</script>

<style scoped>
.comment_listwrapper {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}

.comment_item {
    width: 100%;
    max-width: 100%;
}

.comment_content {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}

.comment_text {
    max-width: 100%;
    overflow: hidden;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
}

.comment_text :deep(img) {
    max-width: 100% !important;
    height: auto !important;
    max-height: 400px !important;
    display: block !important;
    margin: 10px 0 !important;
    border-radius: 4px !important;
    object-fit: contain !important;
}

.comment_text :deep(div) {
    max-width: 100%;
    overflow: hidden;
}

.comment_text :deep(p) {
    margin-bottom: 0.5rem;
    line-height: 1.5;
}

.scrollable {
    max-height: 320px;
    overflow-y: auto;
    padding-right: 8px;
}

.scrollable::-webkit-scrollbar {
    width: 6px;
}

.scrollable::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 3px;
}

.univ-btn,
.attach-btn {
    padding: 10px 26px;
    width: fit-content;
    font-size: 14px;
    margin: 0;
    border-radius: 6px;
}

.attach-btn {
    background-color: rgba(3, 195, 236, 10%);
    color: var(--rep-primary-clr);
    border: 1px solid var(--rep-primary-clr);
}

.spinner-border {
    width: 1.5rem;
    height: 1.5rem;
}

.spinner-border-sm {
    width: 1rem;
    height: 1rem;
}

.comment_text {
    word-wrap: break-word;
}

.ticket_documentInfo {
    padding: 16px;
    border: 1px solid #d7e1ea;
    border-radius: 6px;
    transition: all 0.2s;
}

.ticket_documentInfo:hover {
    border-color: #a6c5e5;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.icon-wrapper {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
    border-radius: 4px;
}

.dots {
    display: inline-block;
    width: 4px;
    height: 4px;
    background-color: #6c757d;
    border-radius: 50%;
    opacity: 0.5;
}

.document_info {
    font-size: 0.8rem;
    color: #6c757d;
}

.document_link {
    color: #0d6efd;
    text-decoration: none;
}

.document_link:hover {
    text-decoration: underline;
}

.document-section .ticket_documentInfo .document_content .document_info .dots {
    background-color: #a6a6a6;
    width: 5px;
    height: 5px;
    border-radius: 50%;
}
</style>